/**
 * Circuits IR-layer endpoints — mirrors ``backend/circuits/router.py``.
 *
 * Two surfaces:
 *
 *   * ``/circuits/versions/{vid}/...`` — read-only on a committed
 *     :SchematicVersion (IR fetch, DRC, ImportDiff, KiCad-net export).
 *   * ``/circuits/sessions/{sid}/snapshots``,
 *     ``/circuits/snapshots/{sid}/...`` — the working buffer surface
 *     (snapshot create / get / mutate / commit / merge-bom / drc /
 *     lineage / BOM CSV download).
 *
 * The full mutation surface stays Python-native (the agent calls the
 * services directly); these HTTP routes exist for the React UI to read
 * snapshots, drive UI-side mutations, and commit drafts.
 */
import { env } from "@/env";
import apiClient from "@lib/axios";
import type {
  BlocksResponse,
  BoardAnnotationSet,
  BomMergeResponse,
  BranchCircuitRequest,
  BranchCircuitResponse,
  CircuitDTO,
  CircuitOp,
  CircuitStatus,
  CircuitVersionRow,
  CommitResponse,
  DrcReport,
  DrcSeverity,
  ImportDiffResponse,
  IrResponse,
  LineageResponse,
  MergeCircuitRequest,
  MergeCircuitResponse,
  MutateResponse,
  PCBOverlayResponse,
  PatchCircuitRequest,
  PromoteCircuitRequest,
  PromoteCircuitResponse,
  SnapshotResponse,
} from "@models/circuit-ir";

export const circuitsApi = {
  // ── Version-scoped reads ────────────────────────────────────────────
  getVersionIr: async (versionId: string): Promise<IrResponse> => {
    const { data } = await apiClient.get<IrResponse>(
      `/circuits/versions/${encodeURIComponent(versionId)}/ir`,
    );
    return data;
  },
  getVersionDrc: async (
    versionId: string,
    severity?: DrcSeverity,
  ): Promise<DrcReport> => {
    const qs = severity ? `?severity=${encodeURIComponent(severity)}` : "";
    const { data } = await apiClient.get<DrcReport>(
      `/circuits/versions/${encodeURIComponent(versionId)}/drc${qs}`,
    );
    return data;
  },
  getVersionBlocks: async (
    versionId: string,
    resolution?: number,
  ): Promise<BlocksResponse> => {
    const qs =
      resolution !== undefined
        ? `?resolution=${encodeURIComponent(resolution)}`
        : "";
    const { data } = await apiClient.get<BlocksResponse>(
      `/circuits/versions/${encodeURIComponent(versionId)}/blocks${qs}`,
    );
    return data;
  },
  getBoardAnnotations: async (
    versionId: string,
  ): Promise<BoardAnnotationSet> => {
    const { data } = await apiClient.get<BoardAnnotationSet>(
      `/circuits/versions/${encodeURIComponent(versionId)}/annotations`,
    );
    return data;
  },
  getImportDiff: async (
    fromVersionId: string,
    toVersionId: string,
  ): Promise<ImportDiffResponse> => {
    const { data } = await apiClient.get<ImportDiffResponse>(
      `/circuits/versions/${encodeURIComponent(
        fromVersionId,
      )}/diff/${encodeURIComponent(toVersionId)}`,
    );
    return data;
  },
  /** Rename-detection diff between an uncommitted snapshot and the
   *  project's currently-committed version. Drives SnapshotReviewPage
   *  -> RenameTheatreDialog after a plugin push.
   *
   *  ``boardId`` is REQUIRED on multi-board projects — without it the
   *  server resolves the alphabetically-first member Board's main
   *  Circuit and the rename theatre fires phantom renames against
   *  the wrong PCB. The plugin push response carries the board id
   *  so the deep link should thread it through. */
  getSnapshotImportDiff: async (
    snapshotId: string,
    projectId: string,
    boardId?: string | null,
    circuitId?: string | null,
  ): Promise<ImportDiffResponse> => {
    const params = new URLSearchParams({ project_id: projectId });
    if (boardId) params.set("board_id", boardId);
    if (circuitId) params.set("circuit_id", circuitId);
    const { data } = await apiClient.get<ImportDiffResponse>(
      `/circuits/snapshots/${encodeURIComponent(
        snapshotId,
      )}/import-diff?${params.toString()}`,
    );
    return data;
  },
  /** Apply user-confirmed suggested renames from RenameTheatreDialog. */
  resolveSnapshotRenames: async (
    snapshotId: string,
    payload: {
      project_id: string;
      /** Board UUID the snapshot was originally pushed against.
       *  REQUIRED on multi-board projects — without it the server
       *  derives the wrong session id and the rename re-snapshot
       *  ends up in a different chain. */
      board_id?: string | null;
      circuit_id?: string | null;
      accepted_new_ids: string[];
    },
  ): Promise<{
    snapshot_id: string;
    parent_snapshot_id: string;
    diff_summary: Record<string, number>;
    review_url: string;
    schema_version: number;
  }> => {
    const { data } = await apiClient.post(
      `/circuits/plugin/snapshots/${encodeURIComponent(
        snapshotId,
      )}/resolve-renames`,
      payload,
    );
    return data;
  },
  exportKicadNetUrl: (versionId: string): string =>
    `${env.VITE_API_BASE_URL}/circuits/versions/${encodeURIComponent(
      versionId,
    )}/export.net`,

  // ── Snapshot lifecycle ──────────────────────────────────────────────
  createSnapshotFromVersion: async (
    sessionId: string,
    versionId: string,
  ): Promise<SnapshotResponse> => {
    const { data } = await apiClient.post<SnapshotResponse>(
      `/circuits/sessions/${encodeURIComponent(sessionId)}/snapshots`,
      { version_id: versionId },
    );
    return data;
  },
  getSnapshot: async (snapshotId: string): Promise<SnapshotResponse> => {
    const { data } = await apiClient.get<SnapshotResponse>(
      `/circuits/snapshots/${encodeURIComponent(snapshotId)}`,
    );
    return data;
  },
  mutateSnapshot: async (
    snapshotId: string,
    ops: CircuitOp[],
    createdByEventId?: string,
  ): Promise<MutateResponse> => {
    const { data } = await apiClient.post<MutateResponse>(
      `/circuits/snapshots/${encodeURIComponent(snapshotId)}/mutate`,
      {
        ops,
        ...(createdByEventId ? { created_by_event_id: createdByEventId } : {}),
      },
    );
    return data;
  },
  commitSnapshot: async (
    snapshotId: string,
    payload: {
      project_id: string;
      /** Board UUID the snapshot was pushed against. REQUIRED on
       *  multi-board projects so the new SchematicVersion attaches
       *  to the right PCB — the server returns 409 with member ids
       *  when this is missing on a multi-board wrapper. */
      board_id?: string | null;
      /** Target Circuit id within the project. Optional for legacy
       *  single-circuit projects; required when the project carries
       *  multiple circuits (Phase 14). Server falls back to the
       *  project's default ``main`` circuit when absent. */
      circuit_id?: string;
      parent_version_id?: string | null;
      description?: string;
      semver_label?: string;
      set_current?: boolean;
      drc_gate?: boolean;
    },
  ): Promise<CommitResponse> => {
    const { data } = await apiClient.post<CommitResponse>(
      `/circuits/snapshots/${encodeURIComponent(snapshotId)}/commit`,
      payload,
    );
    return data;
  },
  mergeBomIntoSnapshot: async (
    snapshotId: string,
    csv: File,
  ): Promise<BomMergeResponse> => {
    const form = new FormData();
    form.append("bom_csv", csv);
    const { data } = await apiClient.post<BomMergeResponse>(
      `/circuits/snapshots/${encodeURIComponent(snapshotId)}/merge-bom`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data;
  },
  exportSnapshotBomCsvUrl: (snapshotId: string): string =>
    `${env.VITE_API_BASE_URL}/circuits/snapshots/${encodeURIComponent(
      snapshotId,
    )}/export.csv`,
  getSnapshotDrc: async (
    snapshotId: string,
    severity?: DrcSeverity,
  ): Promise<DrcReport> => {
    const qs = severity ? `?severity=${encodeURIComponent(severity)}` : "";
    const { data } = await apiClient.get<DrcReport>(
      `/circuits/snapshots/${encodeURIComponent(snapshotId)}/drc${qs}`,
    );
    return data;
  },
  getSnapshotLineage: async (
    snapshotId: string,
    maxDepth = 20,
  ): Promise<LineageResponse> => {
    const { data } = await apiClient.get<LineageResponse>(
      `/circuits/snapshots/${encodeURIComponent(
        snapshotId,
      )}/lineage?max_depth=${maxDepth}`,
    );
    return data;
  },

  // ── Multi-Circuit lifecycle (Phase 14 endpoints) ────────────────────
  /** List every Circuit under a project. Optional status filter is
   *  applied server-side (``?status=draft&status=active``). */
  listCircuits: async (
    projectId: string,
    statusFilter?: CircuitStatus[],
  ): Promise<CircuitDTO[]> => {
    const qs =
      statusFilter && statusFilter.length > 0
        ? `?${statusFilter.map((s) => `status=${encodeURIComponent(s)}`).join("&")}`
        : "";
    const { data } = await apiClient.get<CircuitDTO[]>(
      `/circuits/projects/${encodeURIComponent(projectId)}/circuits${qs}`,
    );
    return data;
  },
  getCircuit: async (circuitId: string): Promise<CircuitDTO> => {
    const { data } = await apiClient.get<CircuitDTO>(
      `/circuits/${encodeURIComponent(circuitId)}`,
    );
    return data;
  },
  /** Newest-first version chain for a single Circuit. Replaces the
   *  legacy ``/projects/{pid}/versions`` when a project has more than one
   *  circuit. The wire shape uses ``version_id`` / ``created_at``; we
   *  remap to the frontend-friendly ``id`` / ``uploaded_at`` so consumers
   *  read the same field names they use everywhere else. */
  getCircuitVersions: async (
    circuitId: string,
  ): Promise<CircuitVersionRow[]> => {
    interface CircuitVersionWire {
      version_id: string;
      version_number: number;
      semver_label: string | null;
      content_hash: string | null;
      source_format: string | null;
      created_at: string;
      component_count: number;
      net_count: number;
      imported: boolean;
      is_current: boolean;
    }
    const { data } = await apiClient.get<CircuitVersionWire[]>(
      `/circuits/${encodeURIComponent(circuitId)}/versions`,
    );
    return data.map((row) => ({
      id: row.version_id,
      version_number: row.version_number,
      semver_label: row.semver_label,
      uploaded_at: row.created_at,
      is_current: row.is_current,
      imported: row.imported,
      source_format: row.source_format,
      component_count: row.component_count,
      net_count: row.net_count,
      description: null,
    }));
  },
  /** Walk the parent_circuit_id chain — root first, input last. Each
   *  entry is a full CircuitDTO so the UI can render name, kind,
   *  status, and the active version inline. */
  getCircuitLineage: async (circuitId: string): Promise<CircuitDTO[]> => {
    const { data } = await apiClient.get<CircuitDTO[]>(
      `/circuits/${encodeURIComponent(circuitId)}/lineage`,
    );
    return data;
  },
  branchCircuit: async (
    sourceCircuitId: string,
    payload: BranchCircuitRequest,
  ): Promise<BranchCircuitResponse> => {
    const { data } = await apiClient.post<BranchCircuitResponse>(
      `/circuits/${encodeURIComponent(sourceCircuitId)}/branch`,
      payload,
    );
    return data;
  },
  mergeCircuits: async (
    targetCircuitId: string,
    payload: MergeCircuitRequest,
  ): Promise<MergeCircuitResponse> => {
    const { data } = await apiClient.post<MergeCircuitResponse>(
      `/circuits/${encodeURIComponent(targetCircuitId)}/merge`,
      payload,
    );
    return data;
  },
  promoteCircuit: async (
    circuitId: string,
    payload: PromoteCircuitRequest = {},
  ): Promise<PromoteCircuitResponse> => {
    const { data } = await apiClient.post<PromoteCircuitResponse>(
      `/circuits/${encodeURIComponent(circuitId)}/promote`,
      payload,
    );
    return data;
  },
  patchCircuit: async (
    circuitId: string,
    payload: PatchCircuitRequest,
  ): Promise<CircuitDTO> => {
    const { data } = await apiClient.patch<CircuitDTO>(
      `/circuits/${encodeURIComponent(circuitId)}`,
      payload,
    );
    return data;
  },
  archiveCircuit: async (circuitId: string): Promise<void> => {
    await apiClient.delete(`/circuits/${encodeURIComponent(circuitId)}`);
  },

  /** PCB overlay for a committed ``:SchematicVersion`` — placements,
   *  tracks, layers per IR plan §2 Layer 3 (Phase 16). 404 on versions
   *  that never carried a ``.kicad_pcb``. */
  getPcbOverlay: async (versionId: string): Promise<PCBOverlayResponse> => {
    const { data } = await apiClient.get<PCBOverlayResponse>(
      `/circuits/versions/${encodeURIComponent(versionId)}/pcb-overlay`,
    );
    return data;
  },
};
